import { FC, useEffect, useState } from "react";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { useDispatch } from "react-redux";
import {
  deleteInvoice,
  getBookingDetails,
  postChatBooking,
} from "../../redux/reducers/Properties/PropertiesSlice";
import { AppDispatch, RootState } from "redux/store";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useLocation } from "react-router-dom";
import GallerySlider from "components/GallerySlider/GallerySlider";
import { BookingRequestDetails } from "redux/reducers/Properties/PropertiesSliceTypes";
import Invoice from "../Booking/Invoice";
import InvoiceUser from "../Booking/InvoiceUser";
import successHandler from "utils/helpers/SuccessHandler";
import { useSelector } from "react-redux";
import { updateBookingsDetails } from "../../redux/reducers/Payments/PaymentReducer";
import { currencySymbol } from "shared/CurrencySymbol";
import moment from "moment";
import NcImage from "shared/NcImage/NcImage";
import { getMessage } from "../../redux/reducers/UserSlice/UserSlice";
import MessageList from "containers/Messages/MessageList";
import "../Messages/MessageList.scss";
import "react-loading-skeleton/dist/skeleton.css";
import BookingDetailsSkeleton from "shared/Skelton/CommonSkelton";
import ReviewForm from "./Review";
import dayjs from "dayjs";
import { Tooltip } from "react-tooltip";
import {
  FilmIcon,
  PaperClipIcon,
  TableCellsIcon,
  CalendarIcon,
  PhoneIcon,
  CheckCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { IoLocationOutline } from "react-icons/io5";
import { FiBarChart2, FiFileText } from "react-icons/fi";
import { BiArchive } from "react-icons/bi";
import { toast } from "react-toastify";
import { FaRegUser } from "react-icons/fa";
import { IoMdCloseCircleOutline } from "react-icons/io";
import FileUploadPreview from "./components/FileUploadPreview";
import BookingProgressTracker from "./components/BookingProgressTracker";
import UserProfileCard from "./components/UserProfileCard";
import PropertySummaryCard from "./components/PropertySummaryCard";
import ReplySection from "./components/ReplySection";
import BookingActions from "./components/BookingActions";
import BookingInfoSummary from "./components/BookingInfoSummary";

export interface BookingRequestProps {
  className?: string;
}

const BookingRequest: FC<BookingRequestProps> = ({ className = "" }) => {
  const [bookingRequestDetails, setBookingRequestDetails] = useState<
    BookingRequestDetails | any
  >(null);
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");
  const [replyText, setReplyText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showUserInvoice, setShowUserInvoice] = useState(false);
  const { currentUser } = useSelector((state: RootState) => state.user);
  const openInvoice = () => setShowInvoice(true);
  const closeInvoice = () => setShowInvoice(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const chatId = bookingRequestDetails?.chat?.id;
  const modifiedBookingMap = chatMessages;
  const [showMore, setShowMore] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const checkoutDate = bookingRequestDetails?.check_out_date;
  const checkinDate = bookingRequestDetails?.check_in_date;
  const isReviewEnabled =
    checkinDate && dayjs().isBefore(dayjs(checkinDate), "day")
      ? false
      : checkoutDate &&
        (dayjs().isAfter(dayjs(checkoutDate).subtract(15, "day"), "day") ||
          dayjs().isAfter(dayjs(checkoutDate), "day"));
  const [previews, setPreviews] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchAllManageBooking();
      setLoading(false);
    };

    fetchData();
  }, [dispatch]);

  const fetchMessages = async (pge: number) => {
    const data = {
      page: pge,
      chatId: bookingRequestDetails?.chat?.id,
    };
    setShowMore(true);

    try {
      const response = await dispatch(getMessage(data)).unwrap();
      setChatMessages(response || []);
    } catch (err: any) {
    } finally {
      setShowMore(false);
    }
  };

  useEffect(() => {
    if (chatId) {
      fetchMessages(+1);
    }
  }, [chatId]);

 


const checkInDate = bookingRequestDetails?.check_in_date 
  ? moment(bookingRequestDetails.check_in_date, "MMMM DD, YYYY").toDate()
  : new Date();

const checkOutDate = bookingRequestDetails?.check_out_date 
  ? moment(bookingRequestDetails.check_out_date, "MMMM DD, YYYY").toDate()
  : new Date();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);
    setSelectedFiles((prev) => [...prev, ...newFiles]);

    newFiles.forEach((file) => {
      const type = file.type;
      if (type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviews((prev) => [
            ...prev,
            {
              type: "image",
              content: reader.result as string,
              name: file.name,
            },
          ]);
        };
        reader.readAsDataURL(file);
      } else if (type === "application/pdf") {
        setPreviews((prev) => [...prev, { type: "pdf", name: file.name }]);
      } else if (type.startsWith("video/")) {
        const maxSizeInBytes = 5 * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
          toast("Video file size should not exceed 5 MB");
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviews((prev) => [
            ...prev,
            {
              type: "video",
              content: reader.result as string,
              name: file.name,
            },
          ]);
        };
        reader.readAsDataURL(file);
      } else if (
        type === "application/msword" ||
        type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        type === "application/vnd.ms-word.document.macroEnabled.12"
      ) {
        setPreviews((prev) => [...prev, { type: "doc", name: file.name }]);
      } else if (
        type === "application/vnd.ms-excel" ||
        type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        type === "application/vnd.ms-excel.sheet.macroEnabled.12"
      ) {
        setPreviews((prev) => [...prev, { type: "excel", name: file.name }]);
      } else if (
        type === "application/vnd.ms-powerpoint" ||
        type ===
          "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
        type === "application/vnd.ms-powerpoint.presentation.macroEnabled.12"
      ) {
        setPreviews((prev) => [
          ...prev,
          { type: "powerpoint", name: file.name },
        ]);
      } else if (type === "text/plain" || type === "text/csv") {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviews((prev) => [
            ...prev,
            { type: "text", content: reader.result as string, name: file.name },
          ]);
        };
        reader.readAsText(file);
      } else if (
        type === "application/zip" ||
        type === "application/x-zip-compressed"
      ) {
        setPreviews((prev) => [...prev, { type: "zip", name: file.name }]);
      } else {
        setPreviews((prev) => [
          ...prev,
          {
            type: "generic",
            name: file.name,
            extension: file.name.split(".").pop() || "",
          },
        ]);
      }
    });
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };
  const fetchAllManageBooking = async () => {
    const action = await dispatch(getBookingDetails(id));
    if (action?.payload?.success) {
      setBookingRequestDetails(action.payload.booking);
    }
  };

  const handleSubmit = async () => {
    if (!replyText.trim() && selectedFiles.length === 0) return;
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("message[body]", replyText);

      selectedFiles.forEach((file) => {
        formData.append("message[attachments][]", file);
      });

      await dispatch(
        postChatBooking({
          chatId: bookingRequestDetails?.chat?.id,
          formData,
        })
      ).unwrap();
      setReplyText("");
      setSelectedFiles([]);
      setPreviews([]);
      fetchAllManageBooking();
      fetchMessages(1);
    } catch (error) {
      console.error("Failed to send reply:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleCancelBooking = async () => {
    if (id == null) {
      console.error("ID is null or undefined");
      return;
    }

    try {
      const action = await dispatch(
        updateBookingsDetails({ id, status: "canceled" })
      );
      if (updateBookingsDetails.fulfilled.match(action)) {
        successHandler("Booking canceled successfully:", action.payload);
        await fetchAllManageBooking();
        fetchMessages(1);
      } else {
        console.error("Failed to cancel booking:", action.payload);
      }
    } catch (error) {
      console.error("Error canceling booking:", error);
    }
  };

  const handleDeleteInvoice = async () => {
    if (id == null) {
      console.error("ID is null or undefined");
      return;
    }

    setIsDeleting(true);

    const postData = {
      bookingId: id,
      id: bookingRequestDetails?.invoice?.id,
      _destroy: true,
      status: "canceled",
    };

    try {
      const action = await dispatch(deleteInvoice(postData));
      if (deleteInvoice.fulfilled.match(action)) {
        successHandler("Invoice deleted successfully:", action.payload);
        await fetchAllManageBooking();
        fetchMessages(1);
      } else {
        console.error("Failed to delete invoice:", action.payload);
      }
    } catch (error) {
      console.error("Error deleting invoice:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const renderFilePreview = (preview: any, index: number) => {
    if (!preview) return null;

    if (preview.type === "image" && preview.content) {
      return (
        <div
          className="w-8 h-8 relative overflow-hidden rounded-sm"
          key={index}
        >
          <img
            src={preview.content}
            alt="Preview"
            className="w-full h-full object-cover"
          />
        </div>
      );
    } else if (preview.type === "video") {
      return <FilmIcon className="w-4 h-4 text-blue-500" key={index} />;
    } else {
      switch (preview.type) {
        case "pdf":
          return <FiFileText className="w-4 h-4 text-red-500" key={index} />;
        case "doc":
          return <FiFileText className="w-4 h-4 text-blue-500" key={index} />;
        case "excel":
          return (
            <TableCellsIcon className="w-4 h-4 text-green-500" key={index} />
          );
        case "powerpoint":
          return (
            <FiBarChart2 className="w-4 h-4 text-orange-500" key={index} />
          );
        case "text":
          return <FiFileText className="w-4 h-4 text-gray-500" key={index} />;
        case "zip":
          return <BiArchive className="w-4 h-4 text-yellow-500" key={index} />;
        default:
          return (
            <div
              className="w-4 h-4 flex items-center justify-center bg-gray-300 dark:bg-gray-700 rounded"
              key={index}
            >
              <span className="text-[10px]">
                {preview.extension?.toUpperCase().substring(0, 3)}
              </span>
            </div>
          );
      }
    }
  };

  const pathArray = [
    { path: 1, type: "Receive a message", status: "canceled" },
    { path: 2, type: "Send invitation to book", status: "booking_request" },
    { path: 3, type: "Wait for confirmation", status: "invitation_sent" },
    {
      path: 4,
      type: "Make payment of first month",
      status: "invoice_sent",
    },
    { path: 5, type: "Booking confirmed", status: "confirmed" },
  ];

  if (loading) {
    return <BookingDetailsSkeleton />;
  }
  return (
    <PayPalScriptProvider options={{ clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || "", currency: "USD", intent: "capture" }}>
    <div className="max-w-7xl mx-auto p-4 lg:container ">
      <h1 className="text-xl font-bold text-gray-700 dark:text-gray-200 mb-6">
        MESSAGE FROM{" "}
        {currentUser?.is_host
          ? bookingRequestDetails?.chat?.sender?.name || "Yopmail"
          : bookingRequestDetails?.chat?.receiver?.name || "Host"}
      </h1>
      <div className="mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 mb-8">
          <div className="lg:col-span-2">
            <BookingProgressTracker
              currentStatus={bookingRequestDetails?.status}
              isHost={currentUser?.is_host || false}
            />

            <div className="p-4 md:p-8 bg-white dark:bg-black rounded-lg mt-5 border border-gray-300 dark:border-gray-700">
              <h2 className="font-semibold mb-4 text-sm sm:text-xl text-gray-700 dark:text-gray-200">
                Booking Request {bookingRequestDetails?.number} for{" "}
                {bookingRequestDetails?.property?.title}
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {moment(checkInDate).format("MMM DD, YYYY")} to{" "}
                    {moment(checkOutDate).format("MMM DD, YYYY")}
                  </span>
                </div>
              </h2>
              <hr />

              <BookingInfoSummary
                invoiceId={bookingRequestDetails?.invoice?.id}
                totalAmount={bookingRequestDetails?.total}
                serviceTax={bookingRequestDetails?.service_tax}
                isHost={currentUser?.is_host || false}
                numberOfGuests={bookingRequestDetails?.number_of_guests}
                checkInDate={checkInDate}
                checkOutDate={checkOutDate}
                numberOfMonths={bookingRequestDetails?.no_of_months}
                isSharedProperty={
                  bookingRequestDetails?.is_shared_property === true ||
                  bookingRequestDetails?.is_shared_property === "true"
                }
                bookedRoom={bookingRequestDetails?.booked_room}
                propertyTitle={bookingRequestDetails?.property?.title}
                bookingStatus={bookingRequestDetails?.status} // ← Add this
              />

              <div className="lg:p-6 lg:pl-0 sm:px-0 sm:py-6 rounded-lg">
                <BookingActions
                  bookingStatus={bookingRequestDetails?.status}
                  isHost={currentUser?.is_host || false}
                  invoiceId={bookingRequestDetails?.invoice?.id}
                  propertySlug={bookingRequestDetails?.property?.slug}
                  showInvoice={showInvoice}
                  showUserInvoice={showUserInvoice}
                  isDeleting={isDeleting}
                  onToggleInvoice={() => setShowInvoice((prev) => !prev)}
                  onToggleUserInvoice={() =>
                    setShowUserInvoice((prev) => !prev)
                  }
                  onDeleteInvoice={handleDeleteInvoice}
                  onCancelBooking={handleCancelBooking}
                  onOpenReview={() => setShowReviewForm(true)}
                  isReviewEnabled={isReviewEnabled}
                  hasReview={!!bookingRequestDetails?.review?.comment}
                  bookingId={bookingRequestDetails?.id}
                  bookingNumber={bookingRequestDetails?.number}
                />

                {/* Keep the review form modal */}
                {showReviewForm && (
                  <div className="fixed inset-x-0 bottom-0 bg-white dark:bg-black shadow-lg p-6 rounded-t-2xl transition-transform transform translate-y-0 animate-slide-up z-50">
                    <ReviewForm
                      bookingRequestDetails={bookingRequestDetails}
                      isOpen={showReviewForm}
                      onClose={() => setShowReviewForm(false)}
                      fetchAllManageBooking={fetchAllManageBooking}
                    />
                  </div>
                )}

                {/* Keep the invoice components */}
                {showUserInvoice && (
                  <InvoiceUser
                    bookingRequestDetails={bookingRequestDetails}
                    setShowUserInvoice={setShowUserInvoice}
                  />
                )}
                {showInvoice && (
                  <Invoice
                    bookingRequestDetails={bookingRequestDetails}
                    closeInvoice={closeInvoice}
                    callback={fetchAllManageBooking}
                    fetchMessages={fetchMessages}
                  />
                )}
              </div>
              <ReplySection
                replyText={replyText}
                setReplyText={setReplyText}
                selectedFiles={selectedFiles}
                previews={previews}
                isSubmitting={isSubmitting}
                handleFileChange={handleFileChange}
                handleRemoveFile={handleRemoveFile}
                handleSubmit={handleSubmit}
              />
              <div className="pt-6">
                <MessageList
                  modifiedBookingMap={modifiedBookingMap}
                  loadMoreMessages={fetchMessages}
                  showMore={showMore}
                />
              </div>
            </div>
          </div>
        </div>
        <div>
         <div>
  {currentUser?.is_host ? (
    <>
      {console.log("Guest user data:", bookingRequestDetails?.user)}
      <UserProfileCard
        user={bookingRequestDetails?.user}
        isHost={false}
        bookingStatus={bookingRequestDetails?.status}
        userLabel="Guest"
      />
    </>
  ) : (
    <>
      {console.log("Host user data:", bookingRequestDetails?.property_user)}
      <UserProfileCard
        user={bookingRequestDetails?.property_user}
        isHost={true}
        bookingStatus={bookingRequestDetails?.status}
        userLabel="Host"
      />
    </>
  )}
</div>
          <div className="mt-5">
            <PropertySummaryCard
              property={bookingRequestDetails?.property}
              bookingRequestDetails={bookingRequestDetails}
              isSharedProperty={
                bookingRequestDetails?.is_shared_property === true ||
                bookingRequestDetails?.is_shared_property === "true"
              }
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              bookingStatus={bookingRequestDetails?.status}
              currentUser={currentUser}  
            />
          </div>
        </div>
      </div>
    </div>
    </PayPalScriptProvider>
  );
};

export default BookingRequest;
